import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import { useFixedRoutes } from '../../context/FixedRoutesContext';
import {
  acceptRoster,
  createRulesDriver,
  fetchRulesDrivers,
  proposeRoster,
  upsertVehicleTags,
} from '../../features/rulesEngine/api';
import {
  buildRosteringDemoBundle,
  buildRosteringLargeDemoBundle,
  ROSTERING_DEMO_ROUTE_IDS,
  ROSTERING_DEMO_ROUTE_TAGS,
  ROSTERING_LARGE_DEMO_ROUTE_IDS,
  type RosteringDemoBundle,
} from '../../features/rulesEngine/demoRoutes';
import { fixedRouteToRosteringSnapshot } from '../../features/rulesEngine/routeSnapshots';
import type { RosteringObjectiveId, RosteringProposal } from '../../features/rulesEngine/types';
import { ROSTERING_OBJECTIVE_OPTIONS } from '../../features/rulesEngine/types';
import RosterTimeline from './RosterTimeline';

function toYmd(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number | string;
  tone?: 'default' | 'success' | 'warning';
}) {
  const theme = useTheme();
  const color =
    tone === 'success'
      ? theme.palette.success.main
      : tone === 'warning'
        ? theme.palette.warning.main
        : theme.palette.text.primary;

  return (
    <Box sx={{ minWidth: 72 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2 }}>
        {label}
      </Typography>
      <Typography variant="subtitle1" fontWeight={600} sx={{ color, lineHeight: 1.3 }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function RosteringPanel() {
  const theme = useTheme();
  const { routes, bulkAddStopsRoutes, rosteringRouteIds, rosteringHandoffNonce } = useFixedRoutes();
  const [serviceDate, setServiceDate] = useState(() => toYmd(new Date()));
  const [selectedRouteIds, setSelectedRouteIds] = useState<string[]>([]);
  const [objective, setObjective] = useState<RosteringObjectiveId>('balanced');
  const [routeTags, setRouteTags] = useState<Record<string, string[]>>({});
  const [driverNameById, setDriverNameById] = useState<Record<string, string>>({});
  const [proposal, setProposal] = useState<RosteringProposal | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const routeColorById = useMemo(
    () => Object.fromEntries(routes.filter((r) => r.color).map((r) => [r.id, r.color as string])),
    [routes]
  );

  const selectableRoutes = useMemo(
    () =>
      routes.filter(
        (r) =>
          (!!r.startTime && !!r.endTime) ||
          (Array.isArray(r.tripSchedules) && r.tripSchedules.length > 0)
      ),
    [routes]
  );

  const selectedObjective =
    ROSTERING_OBJECTIVE_OPTIONS.find((o) => o.id === objective) || ROSTERING_OBJECTIVE_OPTIONS[0];
  const proposalObjective =
    ROSTERING_OBJECTIVE_OPTIONS.find((o) => o.id === (proposal?.objective || objective)) ||
    selectedObjective;

  const panelSx = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 2,
    bgcolor: theme.palette.background.paper,
    p: { xs: 2, md: 2.5 },
  } as const;

  useEffect(() => {
    void fetchRulesDrivers()
      .then((drivers) => {
        setDriverNameById(Object.fromEntries(drivers.map((d) => [d.id, d.display_name])));
      })
      .catch(() => {
        // Keep proposal usable even if driver-name hydration fails.
      });
  }, []);

  // Accept route handoff from Fixed Routes drawer ("Use in Rostering").
  useEffect(() => {
    if (!rosteringHandoffNonce || !rosteringRouteIds.length) return;
    const available = new Set(selectableRoutes.map((r) => r.id));
    const next = rosteringRouteIds.filter((id) => available.has(id));
    if (next.length) {
      setSelectedRouteIds(next);
      setSuccess(
        `Loaded ${next.length} route${next.length === 1 ? '' : 's'} from the Fixed Routes drawer.`
      );
    }
  }, [rosteringHandoffNonce, rosteringRouteIds, selectableRoutes]);

  const loadDemoBundle = async (
    demo: RosteringDemoBundle,
    routeIds: readonly string[],
    label: string
  ) => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      bulkAddStopsRoutes(demo.stops, demo.routes, 'merge');
      setRouteTags((prev) => ({ ...prev, ...demo.routeRequiredTags }));
      setSelectedRouteIds([...routeIds]);

      const tagErrors: string[] = [];
      for (const v of demo.vehicleTags) {
        try {
          await upsertVehicleTags(v.vehicle_id, v.capability_tags);
        } catch {
          tagErrors.push(v.vehicle_id);
        }
      }

      let driversCreated = 0;
      let driverErrors = 0;
      try {
        const existing = await fetchRulesDrivers();
        const knownExternal = new Set(
          existing.map((d) => d.external_driver_id).filter((id): id is string => !!id)
        );
        for (const seed of demo.drivers) {
          if (knownExternal.has(seed.external_driver_id)) continue;
          try {
            await createRulesDriver({
              display_name: seed.display_name,
              external_driver_id: seed.external_driver_id,
              status: 'active',
            });
            driversCreated += 1;
            knownExternal.add(seed.external_driver_id);
          } catch {
            driverErrors += 1;
          }
        }
        // Refresh name map for timeline labels
        const refreshed = await fetchRulesDrivers();
        setDriverNameById(Object.fromEntries(refreshed.map((d) => [d.id, d.display_name])));
      } catch {
        driverErrors = demo.drivers.length;
      }

      const parts = [
        `Loaded ${label}: ${routeIds.length} routes, ${demo.vehicleTags.length} vehicles`,
        driversCreated
          ? `${driversCreated} new driver${driversCreated === 1 ? '' : 's'}`
          : 'drivers already present',
      ];
      const warnings: string[] = [];
      if (tagErrors.length) {
        warnings.push(`vehicle tags failed for ${tagErrors.join(', ')} (need rules_engine_admin)`);
      }
      if (driverErrors) {
        warnings.push(`${driverErrors} driver create(s) failed`);
      }
      setSuccess(
        warnings.length ? `${parts.join('; ')}. Warnings: ${warnings.join('; ')}.` : `${parts.join('; ')}.`
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load demo routes.');
    } finally {
      setBusy(false);
    }
  };

  const loadDemoRoutes = () =>
    void loadDemoBundle(buildRosteringDemoBundle(), ROSTERING_DEMO_ROUTE_IDS, 'small demo');

  const loadLargeDemoRoutes = () =>
    void loadDemoBundle(
      buildRosteringLargeDemoBundle(),
      ROSTERING_LARGE_DEMO_ROUTE_IDS,
      'large demo'
    );

  const generate = async () => {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const selected = selectableRoutes.filter((r) => selectedRouteIds.includes(r.id));
      const snapshots = selected.map((r) => {
        const snap = fixedRouteToRosteringSnapshot(r);
        const tags =
          routeTags[r.id] ||
          ROSTERING_DEMO_ROUTE_TAGS[r.id] ||
          snap.required_vehicle_tags ||
          [];
        return {
          ...snap,
          required_vehicle_tags: tags,
        };
      });
      const result = await proposeRoster({
        service_date: serviceDate,
        route_snapshots: snapshots,
        objective,
      });
      setProposal(result);
      if (!result.stats.total_demands) {
        setError(
          'No demand blocks for this date. Demo routes run Mon–Fri — pick a weekday, or ensure routes have trip schedules / operating hours.'
        );
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to generate roster.');
    } finally {
      setBusy(false);
    }
  };

  const acceptAll = async () => {
    if (!proposal) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const assignments = proposal.assignments
        .filter((a) => a.validation.overallStatus !== 'fail')
        .map((a) => ({
          demand_id: a.demand_id,
          driver_id: a.driver_id ?? null,
          vehicle_id: a.vehicle_id ?? null,
          route_ref: a.route_ref ?? null,
          start_at: a.start_at,
          end_at: a.end_at,
          required_vehicle_tags: a.required_vehicle_tags,
          overrides:
            a.validation.overallStatus === 'warn' && overrideReason.trim()
              ? a.validation.results
                  .filter((r) => r.status === 'fail' && r.severity === 'soft')
                  .map((r) => ({ ruleId: r.ruleId, reason: overrideReason.trim() }))
              : [],
        }));

      const result = await acceptRoster({
        assignments,
      });
      const saved = result.results.filter((x) => x.status === 'saved').length;
      const blocked = result.results.filter((x) => x.status !== 'saved').length;
      setSuccess(`Saved ${saved} duty assignments${blocked ? `, ${blocked} blocked/errors` : ''}.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to accept roster.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box data-testid="rules.rostering.panel" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'flex-start' }}
        spacing={1.5}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Advance Rostering
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Generate a duty proposal from route timings, review the timeline, then accept into the
            duty planner.
          </Typography>
        </Box>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ flexShrink: 0, alignSelf: { xs: 'stretch', sm: 'center' } }}
        >
          <Button
            variant="outlined"
            disabled={busy}
            onClick={loadDemoRoutes}
            data-testid="rules.rostering.load-demo"
          >
            Load demo routes
          </Button>
          <Button
            variant="outlined"
            disabled={busy}
            onClick={loadLargeDemoRoutes}
            data-testid="rules.rostering.load-large-demo"
          >
            Load large demo routes
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* Setup */}
      <Box sx={panelSx}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
          Setup
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {selectedObjective.description} Use <strong>Load large demo routes</strong> for a denser
          network and roster, then re-generate with different objectives to compare unique vehicles
          vs drivers.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'minmax(160px, 180px) minmax(180px, 220px) 1fr',
              md: '180px 220px 1fr auto',
            },
            alignItems: 'start',
          }}
        >
          <TextField
            type="date"
            size="small"
            label="Service date"
            InputLabelProps={{ shrink: true }}
            value={serviceDate}
            onChange={(e) => setServiceDate(e.target.value)}
            fullWidth
            data-testid="rules.rostering.service-date"
          />
          <FormControl size="small" fullWidth>
            <InputLabel id="rostering-objective-label">Objective</InputLabel>
            <Select
              labelId="rostering-objective-label"
              label="Objective"
              value={objective}
              onChange={(e) => setObjective(e.target.value as RosteringObjectiveId)}
              data-testid="rules.rostering.objective-select"
            >
              {ROSTERING_OBJECTIVE_OPTIONS.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel id="rostering-routes-label">Routes</InputLabel>
            <Select
              labelId="rostering-routes-label"
              label="Routes"
              value={selectedRouteIds}
              multiple
              onChange={(e) => setSelectedRouteIds(e.target.value as string[])}
              renderValue={(ids) =>
                (ids as string[])
                  .map((id) => selectableRoutes.find((r) => r.id === id)?.name || id)
                  .join(', ')
              }
              data-testid="rules.rostering.routes-select"
            >
              {selectableRoutes.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name} ({r.startTime || '?—'}–{r.endTime || '?'}) ·{' '}
                  {(r.tripSchedules || []).length} trips
                  {routeTags[r.id]?.length ? ` · tags: ${routeTags[r.id].join(',')}` : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            disabled={busy || selectedRouteIds.length === 0}
            onClick={() => void generate()}
            sx={{ height: 40, px: 2.5, whiteSpace: 'nowrap' }}
            data-testid="rules.rostering.generate"
          >
            Generate roster
          </Button>
        </Box>

        {!selectableRoutes.length && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No fixed routes with timings in this session. Click <strong>Load demo routes</strong> or
            author routes on the map with start/end times or trip schedules.
          </Alert>
        )}
      </Box>

      {proposal && (
        <>
          {/* Results summary + timeline */}
          <Box sx={panelSx}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Box>
                <Typography variant="subtitle2" fontWeight={600}>
                  Proposal
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {proposalObjective.label} · {proposalObjective.description}
                </Typography>
              </Box>
              <Stack
                direction="row"
                spacing={{ xs: 2, sm: 3 }}
                flexWrap="wrap"
                useFlexGap
                sx={{ rowGap: 1.5 }}
              >
                <Stat label="Demands" value={proposal.stats.total_demands} />
                <Stat label="Proposed" value={proposal.stats.proposed} tone="success" />
                <Stat
                  label="Uncovered"
                  value={proposal.stats.uncovered}
                  tone={proposal.stats.uncovered ? 'warning' : 'default'}
                />
                <Stat label="Pass" value={proposal.stats.pass_count} />
                <Stat
                  label="Warn"
                  value={proposal.stats.warn_count}
                  tone={proposal.stats.warn_count ? 'warning' : 'default'}
                />
                {typeof proposal.stats.unique_vehicles === 'number' && (
                  <Stat label="Vehicles" value={proposal.stats.unique_vehicles} />
                )}
                {typeof proposal.stats.unique_drivers === 'number' && (
                  <Stat label="Drivers" value={proposal.stats.unique_drivers} />
                )}
              </Stack>
            </Stack>

            <Divider sx={{ mb: 2 }} />

            <RosterTimeline
              assignments={proposal.assignments}
              demands={proposal.demands}
              uncovered={proposal.uncovered}
              driverNameById={driverNameById}
              routeColorById={routeColorById}
            />
          </Box>

          {/* Accept actions */}
          <Box sx={panelSx}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
              Accept
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Soft warnings need an override reason before they can be saved.
            </Typography>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <TextField
                size="small"
                fullWidth
                label="Soft override reason"
                placeholder="Required when accepting warn rows"
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                data-testid="rules.rostering.override-reason"
              />
              <Button
                variant="contained"
                disabled={busy || proposal.assignments.length === 0}
                onClick={() => void acceptAll()}
                sx={{ flexShrink: 0, height: 40, px: 2.5, whiteSpace: 'nowrap' }}
                data-testid="rules.rostering.accept-all"
              >
                Accept proposed duties
              </Button>
            </Stack>
          </Box>
        </>
      )}
    </Box>
  );
}
